import {
  PopoverOpenButtonRef,
  PopperPopoverOpenButton,
  useScreenWidth,
} from "@msys/ui";
import { Add as AddIcon } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  ButtonBaseProps,
  Fab,
  Typography,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { RestrictedByCapabilityWithDebug } from "../../../auth/RestrictedByCapability.js";
import { RestrictedByProjectPermissionWithDebug } from "../../../auth/RestrictedByProjectPermission.js";
import { useUserData } from "../../../auth/useUserData.js";
import { ALLOW_INVITE_NEW_CONTRACTEE } from "../../../constants.js";
import { CreateInvoiceButton } from "../../invoices/buttons/CreateInvoiceButton.js";
import { PurchaseOrderCreateButton } from "../../purchase-orders/buttons/PurchaseOrderCreateButton.js";
import { CreateQuoteButton } from "../../quotes/buttons/CreateQuoteButton.js";
import { RequestQuoteForOutsourcingButton } from "../../requests/buttons/RequestQuoteForOutsourcingButton.js";
import { RequestQuoteForRequirementButton } from "../../requests/buttons/RequestQuoteForRequirementButton.js";
import { CreateRequirementButton } from "../../requirements/buttons/CreateRequirementButton.js";
import { CreateTodoItemButton } from "../../todos/CreateTodoItemButton.js";
import { useProjectAddEntity } from "../useProjectAddEntity.js";
import { AddProjectContactRelationshipButton } from "./AddProjectContactRelationshipButton.js";
import { AddProjectContentButton } from "./AddProjectContentButton.js";
import { AddProjectFilesButton } from "./AddProjectFilesButton.js";
import { AddProjectMemberButton } from "./AddProjectMemberButton.js";
import { AddProjectMemberCRMButton } from "./AddProjectMemberCRMButton.js";
import { ProjectAddEntityButton_ProjectFragment } from "./ProjectAddEntityButton.generated.js";

const iconStyle = { width: "48px", height: "48px" };

interface Props {
  outgoingQuotesCount: number;
  outgoingRequirementsWithoutWonRequestCount: number;
  project: ProjectAddEntityButton_ProjectFragment;
  refetchQueries: string[];
}

export const ProjectAddEntityButton = ({
  outgoingQuotesCount,
  outgoingRequirementsWithoutWonRequestCount,
  project,
  refetchQueries,
}: Props) => {
  const navigate = useNavigate();
  const { t } = useTranslate([
    "ProjectOverview",
    "Global",
    "CrmUsers",
    "Contents",
    "FilesBoxTable",
  ]);
  const { isMinTablet } = useScreenWidth();
  const viewer = useUserData().currentUser!;

  const popoverOpenButtonRef = React.useRef<PopoverOpenButtonRef>();

  const showContractee =
    viewer.organisation.capabilities.includes("quoting") &&
    project.crmOrganisation;
  const canInviteContractee = project.canInviteContractee;

  const hasRequirementsForRequest =
    outgoingRequirementsWithoutWonRequestCount > 0;
  const hasQuotesForRequest = outgoingQuotesCount > 0;

  const { handleNavigateToPlanning } = useProjectAddEntity({
    projectId: project.id,
  });

  return (
    <>
      <PopperPopoverOpenButton
        keepMounted
        popoverRef={popoverOpenButtonRef}
        placement="top-end"
        modifiers={[
          {
            name: "offset",
            options: {
              offset: [0, 8],
            },
          },
        ]}
        content={
          <Box
            sx={{
              display: "flex",
              p: 1,
              flexDirection: "row",
              gap: 1,
              flexWrap: "wrap",
              maxWidth: isMinTablet ? 424 : 320,
            }}
          >
            {/* requirements */}
            <RestrictedByCapabilityWithDebug capability="requirements">
              <RestrictedByProjectPermissionWithDebug
                permission="requirement_document_read"
                project={project}
              >
                <RestrictedByProjectPermissionWithDebug
                  permission="requirement_document_update"
                  project={project}
                >
                  <CreateRequirementButton
                    projectId={project.id}
                    Button={
                      <ButtonWithIcon
                        title={t("Requirement", { ns: "ProjectOverview" })}
                        icon={
                          <img
                            src="/entities/requirement.svg"
                            style={iconStyle}
                          />
                        }
                      />
                    }
                  />
                </RestrictedByProjectPermissionWithDebug>
              </RestrictedByProjectPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>

            {/* quotes */}
            <RestrictedByCapabilityWithDebug capability="quoting">
              <RestrictedByProjectPermissionWithDebug
                permission="quote_document_read"
                project={project}
              >
                <RestrictedByProjectPermissionWithDebug
                  permission="quote_document_update"
                  project={project}
                >
                  {(!(project.state === "opportunity") ||
                    project.incomingQuoteRequests.length === 0 ||
                    project.incomingQuoteRequests.some(
                      request =>
                        request.status === "PUBLISHED" ||
                        request.wonBySystemOrganisationId ===
                          viewer.organisation.id
                    )) && (
                    <CreateQuoteButton
                      projectId={project.id}
                      pathToProject={`/projects/${project.id}`}
                      Button={
                        <ButtonWithIcon
                          title={t("Quote", { ns: "ProjectOverview" })}
                          icon={
                            <img src="/entities/quote.svg" style={iconStyle} />
                          }
                        />
                      }
                    />
                  )}
                </RestrictedByProjectPermissionWithDebug>
              </RestrictedByProjectPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>

            {/* order */}
            <RestrictedByCapabilityWithDebug capability="ordering">
              <RestrictedByProjectPermissionWithDebug
                permission="quote_document_update"
                project={project}
              >
                <PurchaseOrderCreateButton
                  projectId={project.id}
                  Button={
                    <ButtonWithIcon
                      title={t("Order", { ns: "ProjectOverview" })}
                      icon={<img src="/entities/order.svg" style={iconStyle} />}
                    />
                  }
                  handleOrderCreate={orderId => {
                    navigate(
                      `/projects/${project.id}/materials/purchase-orders/${orderId}/edit`
                    );
                  }}
                />
              </RestrictedByProjectPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>

            {/* invoices */}
            <RestrictedByCapabilityWithDebug capability="invoicing">
              <RestrictedByProjectPermissionWithDebug
                permission="quote_document_read"
                project={project}
              >
                <RestrictedByProjectPermissionWithDebug
                  permission="quote_document_update"
                  project={project}
                >
                  <CreateInvoiceButton
                    projectId={project.id}
                    Button={
                      <ButtonWithIcon
                        title={t("Invoice", { ns: "ProjectOverview" })}
                        icon={
                          <img src="/entities/invoice.svg" style={iconStyle} />
                        }
                      />
                    }
                  />
                </RestrictedByProjectPermissionWithDebug>
              </RestrictedByProjectPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>

            {/* my team */}
            <RestrictedByProjectPermissionWithDebug
              permission="project_member_read"
              project={project}
            >
              <RestrictedByProjectPermissionWithDebug
                permission="project_member_create"
                project={project}
              >
                <AddProjectMemberButton
                  projectId={project.id}
                  Button={
                    <ButtonWithIcon
                      title={t("My team", { ns: "ProjectOverview" })}
                      icon={
                        <img src="/entities/client-add.svg" style={iconStyle} />
                      }
                    />
                  }
                  refetchQueries={refetchQueries}
                />
              </RestrictedByProjectPermissionWithDebug>
            </RestrictedByProjectPermissionWithDebug>

            {/* client */}
            {ALLOW_INVITE_NEW_CONTRACTEE &&
              showContractee &&
              canInviteContractee && (
                <RestrictedByProjectPermissionWithDebug
                  permission="project_member_read"
                  project={project}
                >
                  <RestrictedByProjectPermissionWithDebug
                    permission="project_project_update"
                    project={project}
                  >
                    <AddProjectMemberCRMButton
                      projectId={project.id}
                      Button={
                        <ButtonWithIcon
                          title={t("Client", { ns: "ProjectOverview" })}
                          icon={
                            <img
                              src="/entities/client-add.svg"
                              style={iconStyle}
                            />
                          }
                        />
                      }
                      refetchQueries={refetchQueries}
                    />
                  </RestrictedByProjectPermissionWithDebug>
                </RestrictedByProjectPermissionWithDebug>
              )}

            {/* contact */}
            <AddProjectContactRelationshipButton
              project={project}
              Button={
                <ButtonWithIcon
                  title={t("Contact", { ns: "ProjectOverview" })}
                  icon={<img src="/entities/contact.svg" style={iconStyle} />}
                />
              }
              handleComplete={() => {
                popoverOpenButtonRef.current?.handleClose();
              }}
            />

            {/* request */}
            {viewer.organisation.capabilities.includes("quoting")
              ? hasQuotesForRequest && (
                  <RestrictedByProjectPermissionWithDebug
                    permission="requirement_share_create"
                    project={project}
                  >
                    <RequestQuoteForOutsourcingButton
                      projectId={project.id}
                      Button={
                        <ButtonWithIcon
                          title={t("Subcontract", { ns: "ProjectOverview" })}
                          icon={
                            <img
                              src="/entities/subcontract.svg"
                              style={iconStyle}
                            />
                          }
                        />
                      }
                    />
                  </RestrictedByProjectPermissionWithDebug>
                )
              : hasRequirementsForRequest && (
                  <RestrictedByProjectPermissionWithDebug
                    permission="requirement_share_create"
                    project={project}
                  >
                    <RequestQuoteForRequirementButton
                      projectId={project.id}
                      Button={
                        <ButtonWithIcon
                          title={t("Contract", { ns: "ProjectOverview" })}
                          icon={
                            <img
                              src="/entities/subcontract.svg"
                              style={iconStyle}
                            />
                          }
                        />
                      }
                    />
                  </RestrictedByProjectPermissionWithDebug>
                )}

            {/* planning */}
            <RestrictedByCapabilityWithDebug capability="planning">
              <RestrictedByProjectPermissionWithDebug
                permission="project_project_update"
                project={project}
              >
                {viewer.organisation.id ===
                  project.owningSystemOrganisationId && (
                  <ButtonWithIcon
                    title={t("Planning", { ns: "ProjectOverview" })}
                    icon={
                      <img src="/entities/planning.svg" style={iconStyle} />
                    }
                    onClick={handleNavigateToPlanning}
                  />
                )}
              </RestrictedByProjectPermissionWithDebug>
            </RestrictedByCapabilityWithDebug>

            {/* to-do */}
            <CreateTodoItemButton
              linkedProjectId={project.id}
              Button={
                <ButtonWithIcon
                  title={t("To-do", { ns: "ProjectOverview" })}
                  icon={<img src="/entities/todo.svg" style={iconStyle} />}
                />
              }
              refetchQueries={refetchQueries}
            />

            {/* files */}
            <AddProjectFilesButton
              projectId={project.id}
              Button={
                <ButtonWithIcon
                  title={t("Files", { ns: "ProjectOverview" })}
                  icon={
                    <img src="/entities/attachment.svg" style={iconStyle} />
                  }
                />
              }
              handleClick={() => {
                popoverOpenButtonRef.current?.handleClose();
              }}
            />

            {/* content */}
            <AddProjectContentButton
              project={project}
              Button={
                <ButtonWithIcon
                  title={t("Content", { ns: "ProjectOverview" })}
                  icon={<img src="/entities/info.svg" style={iconStyle} />}
                />
              }
            />
          </Box>
        }
      >
        <Fab size={isMinTablet ? "large" : "medium"} color="primary">
          <AddIcon />
        </Fab>
      </PopperPopoverOpenButton>
    </>
  );
};

function ButtonWithIcon({
  title,
  icon,
  ...props
}: {
  title: string;
  icon: React.ReactElement;
} & Omit<ButtonBaseProps, "title">) {
  return (
    <ButtonBase
      sx={theme => ({
        transition: "background-color 0.2s ease-out",
        backgroundColor: theme.palette.common.white,
        ["&:hover:not(:has(button:hover)):not(:has(a:hover))"]: {
          backgroundColor: theme.palette.grey[100],
        },
        ["&:active:not(:has(button:active)):not(:has(a:active))"]: {
          backgroundColor: theme.palette.grey[200],
        },
        borderRadius: "4px",
        display: "flex",
        flexDirection: "column",
        width: "96px",
        minHeight: "80px",
      })}
      focusRipple
      {...props}
    >
      <div style={{ display: "flex" }}>{icon}</div>
      <Typography variant="body2" align="center">
        {title}
      </Typography>
    </ButtonBase>
  );
}
